import React, { useState, useEffect, useRef, useCallback } from "react"

export default function SimpleBeforeAfter({
  beforeImage,
  afterImage,
  beforeAlt,
  afterAlt,
  mode,
  showModeOnly = false,
}) {
  const [position, setPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef(null)
  const dimensionsRef = useRef({ left: 0, width: 0 })

  // Cache dimensions and only update on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { left, width } = containerRef.current.getBoundingClientRect()
        dimensionsRef.current = { left, width }
      }
    }

    updateDimensions()
    window.addEventListener("resize", updateDimensions)
    return () => window.removeEventListener("resize", updateDimensions)
  }, [])

  const handleMove = useCallback(
    (e) => {
      if (!isDragging) return
      const { left, width } = dimensionsRef.current
      const x = e.clientX - left
      const newPosition = Math.max(0, Math.min(100, (x / width) * 100))
      setPosition(newPosition)
    },
    [isDragging]
  )

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return
      e.preventDefault()
      const touch = e.touches[0]
      const { left, width } = dimensionsRef.current
      const x = touch.clientX - left
      const newPosition = Math.max(0, Math.min(100, (x / width) * 100))
      setPosition(newPosition)
    },
    [isDragging]
  )

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMove)
      window.addEventListener("mouseup", () => setIsDragging(false))
    }
    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", () => setIsDragging(false))
    }
  }, [isDragging, handleMove])

  const getUpscaleTypeStyle = (mode) => {
    switch (mode) {
      case "upscale":
        return "bg-gradient-to-r from-blue-500 to-cyan-400"
      case "upscale-basic":
        return "bg-gradient-to-r from-emerald-600 to-green-400"
      case "upscale-flux":
        return "bg-gradient-to-r from-orange-500 to-amber-400"
      case "style-transfer":
        return "bg-gradient-to-r from-purple-500 to-pink-400"
      case "create":
        return "bg-gradient-to-r from-purple-600 to-indigo-500"
      case "inpaint":
        return "bg-gradient-to-r from-pink-500 to-rose-400"
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-400"
    }
  }

  const getModeLabel = (mode) => {
    switch (mode) {
      case "style-transfer":
        return "Style Transfer"
      case "upscale":
        return "Creative"
      case "upscale-basic":
        return "Crystal"
      case "upscale-flux":
        return "Flux"
      case "create":
        return "Create"
      case "inpaint":
        return "Edit"
      default:
        return "Standard"
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full select-none overflow-hidden rounded-lg"
      style={{ cursor: isDragging ? "col-resize" : "pointer" }}
      onMouseDown={() => setIsDragging(true)}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
      onTouchCancel={() => setIsDragging(false)}
      onTouchMove={handleTouchMove}
    >
      {/* After Image (Hintergrund) */}
      <img
        src={afterImage}
        alt={afterAlt}
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchpriority="high"
      />

      {/* Before Image (mit Clip) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={beforeImage}
          alt={beforeAlt}
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-[1px] bg-white"
        style={{
          left: `${position}%`,
          transform: "translateX(-50%)",
        }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center gap-3">
          <div className="w-3 h-3 border-t-2 border-l-2 border-white transform -rotate-45"></div>
          <div className="w-3 h-3 border-t-2 border-r-2 border-white transform rotate-45"></div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute text-xs text-black bg-white/70 rounded-xl font-bold px-3 py-1 top-4 left-4">
        Before
      </div>
      <div className="absolute text-xs text-white rounded-xl font-bold px-3 py-1 top-4 right-4 bg-gradient-to-r via-blue-500 to-emerald-600 from-sky-400 background-animate">
        After
      </div>

      {/* Mode Label - Bottom Right */}
      {showModeOnly && mode && (
        <div className="absolute bottom-4 right-4 z-10 pointer-events-none">
          <div
            className={`flex items-center px-2 py-1 rounded-lg ${getUpscaleTypeStyle(
              mode
            )}`}
          >
            <span className="text-xs font-bold text-white">
              {getModeLabel(mode)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
